import { Check, ChevronDown } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MODE_ORDER, MODES } from "@/lib/research";
import { cn } from "@/lib/utils";

// Chat / Fast Search / Deep Search picker that sits next to the send button.
export function ModePicker({ research, testIdPrefix, disabled }) {
  const current = MODES[research.mode];
  const Icon = current.icon;
  const searching = research.mode !== "chat";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={`Mode: ${current.label}. Change mode`}
          data-testid={`${testIdPrefix}-mode-picker`}
          data-mode={research.mode}
          className={cn(
            "flex h-9 shrink-0 items-center gap-1.5 rounded-full border px-2.5 text-[13px] font-medium transition-[background-color,border-color,color] duration-150 disabled:opacity-50",
            searching ? "border-white/25 bg-white/[0.09] text-white hover:bg-white/[0.13]" : "border-white/[0.1] text-zinc-400 hover:border-white/20 hover:text-zinc-100",
          )}
        >
          <Icon className="h-4 w-4" strokeWidth={1.8} />
          <span className={cn(searching ? "inline" : "hidden sm:inline")} data-testid={`${testIdPrefix}-mode-label`}>
            <span className="sm:hidden">{searching ? current.label.split(" ")[0] : current.label}</span>
            <span className="hidden sm:inline">{current.label}</span>
          </span>
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" side="top" sideOffset={8} className="w-[300px] border-white/10 bg-ink-850 p-1.5" data-testid={`${testIdPrefix}-mode-menu`}>
        <DropdownMenuLabel className="px-2.5 pb-1 pt-1.5 text-[11px] font-medium uppercase tracking-[0.14em] text-zinc-500">Mode</DropdownMenuLabel>
        {MODE_ORDER.map((key) => {
          const m = MODES[key];
          const ItemIcon = m.icon;
          const active = key === research.mode;
          return (
            <DropdownMenuItem
              key={key}
              onSelect={() => research.setMode(key)}
              data-testid={`${testIdPrefix}-mode-option-${key}`}
              data-active={active ? "true" : "false"}
              className="flex items-start gap-3 rounded-lg px-2.5 py-2.5 focus:bg-white/[0.06]"
            >
              <ItemIcon className="mt-0.5 h-[18px] w-[18px] shrink-0 text-zinc-200" strokeWidth={1.6} />
              <span className="min-w-0 flex-1">
                <span className="block text-[14px] font-medium text-white">{m.label}</span>
                <span className="mt-0.5 block text-[12px] leading-snug text-zinc-500">{m.blurb}</span>
              </span>
              <Check className={cn("mt-0.5 h-4 w-4 shrink-0 text-white transition-opacity", active ? "opacity-100" : "opacity-0")} />
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
